import React, { useState } from 'react';
import { Tabs } from 'antd';
import type { TabsProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import { usePickupSiteV2Gray } from '../../../hooks/usePickupSiteV2Gray';
import CooperationWarehousePage from './CooperationWarehousePage';
import AddressReviewPoolPage from '../address-review-pool/AddressReviewPoolPage';

/**
 * 揽收站点入口（揽收站点 2.0 · 信息架构）
 *
 * PRD：审核池不是独立菜单，而是「揽收站点」菜单下的一个顶部页签，与原有内容并列：
 *  - 地址审核池   —— 2.0 新增（内部再分 新地址审核 / 司机反馈跟进）
 *  - 揽收站点管理 —— 原有「揽收站点」全部内容（CooperationWarehousePage）
 *
 * 灰度：仅当前 hub 在 2.0 名单时展示双页签；非灰度 hub 保持 1.0 行为（无页签，直接原内容）。
 */
const PickupSitePage: React.FC = () => {
  const { t } = useTranslation();
  const { currentWarehouse } = useWarehouseContext();
  const { express2Enabled } = usePickupSiteV2Gray();
  const isV2 = express2Enabled((currentWarehouse as any)?.id);
  const [activeTab, setActiveTab] = useState('review-pool');
  // 页签切换计数：antd Tabs 默认保留非活跃面板不卸载，切回来会一直显示切走前的数据。
  // 这里发刷新信号让子页重查，而不是 destroyInactiveTabPane 销毁重建——后者会把筛选、
  // 页码、选中卡片和页签徽标计数一并丢掉。
  const [refreshSignal, setRefreshSignal] = useState(0);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setRefreshSignal((n) => n + 1);
  };

  // 非灰度 hub：保持 1.0，仅原有揽收站点管理，不加页签
  if (!isV2) {
    return <CooperationWarehousePage />;
  }

  const items: TabsProps['items'] = [
    {
      key: 'review-pool',
      label: t('pickup.addressReviewPool.title'),
      // 审核池（内部含 新地址审核 / 司机反馈跟进 子页签）
      children: <AddressReviewPoolPage refreshSignal={refreshSignal} />,
    },
    {
      key: 'site-management',
      label: t('pickup.cooperationWarehouse.tabManagement'),
      // 揽收站点管理 = 原有揽收站点页面（真实内容），不做替换
      children: <CooperationWarehousePage refreshSignal={refreshSignal} />,
    },
  ];

  return (
    <div className="h-full flex flex-col">
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={items}
        className="h-full [&_.ant-tabs-content]:h-full [&_.ant-tabs-tabpane]:h-full"
      />
    </div>
  );
};

export default PickupSitePage;
